import React, { useEffect, useRef, useState } from 'react';
import SiteFooter from '../components/SiteFooter';
import { trackPurchase } from '../lib/track';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const rupee = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
const WORKSHOP_START = new Date('2026-09-24T17:00:00+05:30');

const OUTCOMES = [
  { t: 'Read setups in real time', d: 'Identify high-probability stock-options setups as the market moves.' },
  { t: 'Control risk on every trade', d: 'Master position sizing and stop-loss discipline for consistency.' },
  { t: 'Trade with clear rules', d: 'Build defined entry, exit and trade-management rules you can repeat.' },
  { t: 'Shortlist stocks fast', d: 'Use ready-to-use scanners to find the right stocks in minutes.' },
];

const DAYS = [
  {
    no: 'Day 01', when: '24 September · 5:00–7:00 PM IST', title: 'Foundation & Framework',
    points: [
      'Difference between stock options and index trading',
      'Key distinctions between intraday and swing trading',
      'How to structure your watchlist and daily routine',
    ],
  },
  {
    no: 'Day 02', when: '25 September · 5:00–7:00 PM IST', title: 'Live Strategy & Execution',
    points: [
      'Intraday options trading setup with clear entry & exit rules',
      'Practical trade examples and scanner-based stock selection',
      'Managing trades, stop-loss and profit booking under live conditions',
    ],
  },
];

const AUDIENCE = [
  'Beginners who want a structured options approach',
  'Working professionals who want to trade part-time',
  'Traders who want a rule-based options system',
  'Anyone moving from index to stock options',
];

const STEPS = [
  'The live session is conducted online on Zoom — join through our website and app.',
  'Session recordings will not be provided; study material (if available) is shared within 24 hours.',
  'Join the premium Telegram community for workshop updates and to get your doubts solved.',
  'The Telegram group link is shared with you right after registration.',
];

const FAQS = [
  { q: 'Will Chirag sir answer my questions?', a: 'Yes. Time is reserved during the workshop for your doubts, and the Telegram community hosts ongoing discussions for workshop-related questions.' },
  { q: 'Will this workshop be conducted live?', a: 'Yes, both sessions are conducted live online on 24th & 25th September, 5:00–7:00 PM IST.' },
  { q: 'Can I skip a session?', a: 'We recommend attending both live sessions, as recordings will not be available.' },
  { q: 'Will I get recordings?', a: 'No, recordings of the workshop will not be provided.' },
  { q: 'Will I get trades or tips in the Telegram group?', a: 'No. The Telegram group is strictly for educational discussion, learning and doubt-solving — not for tips, calls or buy/sell recommendations.' },
  { q: 'Is the workshop fee refundable?', a: 'No. The workshop fee is non-refundable once payment is completed.' },
];

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.async = true;
    const t = setTimeout(() => resolve(false), 10000);
    s.onload = () => { clearTimeout(t); resolve(!!window.Razorpay); };
    s.onerror = () => { clearTimeout(t); resolve(false); };
    document.body.appendChild(s);
  });

export default function WorkshopPage() {
  const [info, setInfo] = useState(null);
  const [form, setForm] = useState({ name: '', phone: '', email: '' });
  const [coupon, setCoupon] = useState('');
  const [applied, setApplied] = useState(null); // {valid, discount_amount, final_amount, coupon_id}
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [success, setSuccess] = useState(null);
  const [openFaq, setOpenFaq] = useState(0);
  const [cd, setCd] = useState({ d: 0, h: 0, m: 0, s: 0 });
  const formRef = useRef(null);

  useEffect(() => {
    fetch(`${API_URL}/api/workshop/info`).then((r) => r.json()).then(setInfo).catch(() => {});
  }, []);

  useEffect(() => {
    const tick = () => {
      const diff = Math.max(0, WORKSHOP_START - new Date());
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setCd({ d, h, m, s });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  const mrp = info?.mrp ?? 20000;
  const basePrice = info?.price ?? 15000;
  const payable = applied?.valid ? applied.final_amount : basePrice;

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });

  const applyCoupon = async () => {
    const code = coupon.trim().toUpperCase();
    if (!code) return;
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) {
      toast.error('Enter your email above before applying a coupon');
      return;
    }
    setApplyingCoupon(true);
    try {
      const res = await fetch(`${API_URL}/api/coupons/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          coupon_code: code,
          subscription_type: info?.coupon_type || 'workshop',
          user_email: form.email.trim().toLowerCase(),
          original_amount: basePrice,
        }),
      });
      const data = await res.json();
      if (data.valid) {
        setApplied({ ...data, code });
        toast.success(`Coupon applied — you save ${rupee(data.discount_amount)}`);
      } else {
        setApplied(null);
        toast.error(data.message || 'Invalid coupon code');
      }
    } catch {
      toast.error('Could not validate coupon. Try again.');
    } finally {
      setApplyingCoupon(false);
    }
  };

  const removeCoupon = () => { setApplied(null); setCoupon(''); };

  const handlePay = async () => {
    if (!form.name.trim() || !/^[6-9]\d{9}$/.test(form.phone.trim()) || !/^\S+@\S+\.\S+$/.test(form.email.trim())) {
      toast.error('Please enter a valid name, 10-digit phone and email');
      return;
    }
    setProcessing(true);
    try {
      const ok = await loadRazorpayScript();
      if (!ok) throw new Error('Could not load payment gateway. Please refresh and try again.');

      const orderRes = await fetch(`${API_URL}/api/workshop/create-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, coupon_code: applied?.valid ? applied.code : '' }),
      });
      if (!orderRes.ok) throw new Error('Could not start payment. Please try again.');
      const order = await orderRes.json();

      const options = {
        key: order.razorpay_key_id,
        order_id: order.order_id,
        amount: Math.round(order.amount * 100),
        currency: order.currency,
        name: 'Upper Circuit Stocks',
        description: order.title,
        prefill: { name: form.name, email: form.email, contact: form.phone },
        theme: { color: '#E8B33C' },
        handler: async (resp) => {
          try {
            const verifyRes = await fetch(`${API_URL}/api/workshop/verify-payment`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                razorpay_order_id: resp.razorpay_order_id,
                razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_signature: resp.razorpay_signature,
              }),
            });
            if (!verifyRes.ok) throw new Error('verify failed');
            const data = await verifyRes.json();
            trackPurchase({ value: order.amount, transaction_id: resp.razorpay_payment_id });
            setSuccess(data);
            window.scrollTo({ top: 0, behavior: 'smooth' });
          } catch {
            toast.error('Payment captured but verification failed. Please contact support on WhatsApp.');
          } finally {
            setProcessing(false);
          }
        },
        modal: { ondismiss: () => setProcessing(false) },
      };
      new window.Razorpay(options).open();
    } catch (e) {
      toast.error(e.message || 'Something went wrong');
      setProcessing(false);
    }
  };

  return (
    <>
      <div id="ucs-workshop">
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Anton&family=Cinzel:wght@600;700&family=IBM+Plex+Mono:wght@400;500;600&family=Manrope:wght@400;500;600;700&display=swap');
          #ucs-workshop{--ink:#0A0704;--panel:#16110A;--line:rgba(232,179,60,.22);--gold:#E8B33C;--gold-lt:#FFE9A8;--bull:#2FBF6E;--bear:#C4342B;--text:#EFE7D6;--muted:#A2937A;background:#0A0704;color:var(--text);font-family:'Manrope',system-ui,sans-serif;font-size:17px;line-height:1.62;-webkit-font-smoothing:antialiased;position:relative;overflow-x:hidden}
          #ucs-workshop *{box-sizing:border-box}
          #ucs-workshop p{margin:0 0 14px}
          #ucs-workshop h1,#ucs-workshop h2,#ucs-workshop h3{margin:0;font-weight:400}
          #ucs-workshop .w-wrap{width:100%;max-width:1120px;margin:0 auto;padding:0 20px}
          #ucs-workshop .w-display{font-family:'Anton',Impact,sans-serif;letter-spacing:.4px;line-height:1.04;text-transform:uppercase}
          #ucs-workshop h1.w-display{font-size:clamp(38px,7vw,66px)}
          #ucs-workshop h2.w-display{font-size:clamp(27px,4.4vw,42px)}
          #ucs-workshop .w-eyebrow{font-family:'Cinzel',serif;font-weight:700;letter-spacing:.24em;text-transform:uppercase;font-size:12px;color:var(--gold)}
          #ucs-workshop .w-mono{font-family:'IBM Plex Mono',monospace}
          #ucs-workshop .w-gold{background:linear-gradient(178deg,#FFF6C8 0%,#F3C453 42%,#FFEFA8 56%,#C08A1C 100%);-webkit-background-clip:text;background-clip:text;color:transparent}
          #ucs-workshop .w-lede{font-size:clamp(16px,2.2vw,20px);color:#D9CDB6}
          #ucs-workshop .w-muted{color:var(--muted)}
          #ucs-workshop .w-ticker{border-bottom:1px solid var(--line);background:#0D0A05;overflow:hidden;white-space:nowrap}
          #ucs-workshop .w-track{display:inline-flex;animation:wSlide 32s linear infinite}
          #ucs-workshop .w-track span{font-family:'IBM Plex Mono',monospace;font-size:12px;letter-spacing:.16em;padding:9px 22px;color:var(--gold);text-transform:uppercase;opacity:.85}
          @keyframes wSlide{from{transform:translateX(0)}to{transform:translateX(-50%)}}
          #ucs-workshop .w-hero{position:relative;padding:44px 0 10px}
          #ucs-workshop .w-hero::before{content:"";position:absolute;inset:-120px 0 auto 0;height:600px;pointer-events:none;background:radial-gradient(58% 50% at 50% 24%,rgba(196,140,20,.28),transparent 70%)}
          #ucs-workshop .w-herogrid{position:relative;display:grid;grid-template-columns:1fr;gap:34px;align-items:start}
          @media(min-width:960px){#ucs-workshop .w-herogrid{grid-template-columns:1.15fr .85fr}}
          #ucs-workshop .w-facts{display:grid;grid-template-columns:repeat(2,1fr);gap:10px;margin:26px 0 0}
          #ucs-workshop .w-fact{border:1px solid var(--line);border-radius:10px;background:linear-gradient(180deg,rgba(232,179,60,.07),rgba(232,179,60,.02));padding:12px 13px}
          #ucs-workshop .w-fact .k{font-family:'IBM Plex Mono',monospace;font-size:10.5px;letter-spacing:.18em;color:var(--muted);text-transform:uppercase}
          #ucs-workshop .w-fact .v{font-family:'IBM Plex Mono',monospace;font-size:15px;font-weight:600;color:var(--gold-lt);margin-top:4px}
          #ucs-workshop .w-card{background:linear-gradient(180deg,#1A1409,#110D07);border:1px solid rgba(232,179,60,.42);border-radius:18px;box-shadow:0 26px 60px rgba(0,0,0,.62),inset 0 1px 0 rgba(255,232,168,.14);padding:24px 22px;position:relative}
          #ucs-workshop .w-price{display:flex;align-items:flex-end;gap:12px}
          #ucs-workshop .w-price .now{font-family:'Anton',sans-serif;font-size:44px;color:var(--gold-lt);line-height:1}
          #ucs-workshop .w-price .was{font-size:20px;color:#8a7d66;text-decoration:line-through;margin-bottom:5px}
          #ucs-workshop .w-price .off{font-family:'IBM Plex Mono',monospace;font-size:12px;color:var(--bull);border:1px solid rgba(47,191,110,.4);border-radius:999px;padding:3px 9px;margin-bottom:8px}
          #ucs-workshop label{display:block;font-family:'IBM Plex Mono',monospace;font-size:10.5px;letter-spacing:.16em;text-transform:uppercase;color:var(--muted);margin-bottom:6px}
          #ucs-workshop input{width:100%;padding:13px 14px;border-radius:10px;background:#0B0803;border:1px solid rgba(232,179,60,.26);color:var(--text);font:inherit;font-size:16px;transition:border-color .15s,box-shadow .15s}
          #ucs-workshop input:focus{outline:none;border-color:var(--gold);box-shadow:0 0 0 3px rgba(232,179,60,.16)}
          #ucs-workshop input::placeholder{color:#5E5340}
          #ucs-workshop .w-btn{display:block;width:100%;border:0;cursor:pointer;border-radius:12px;padding:16px 18px;font-family:'Anton',sans-serif;font-size:19px;letter-spacing:.6px;text-transform:uppercase;color:#2A1B02;background:linear-gradient(180deg,#FFE38F,#EDB63F 46%,#C88A18);box-shadow:0 10px 24px rgba(200,138,24,.30),inset 0 1px 0 rgba(255,255,255,.5);transition:transform .12s ease,box-shadow .12s ease;text-align:center}
          #ucs-workshop .w-btn:hover{transform:translateY(-1px)}
          #ucs-workshop .w-btn[disabled]{opacity:.6;cursor:wait}
          #ucs-workshop .w-cbtn{border:1px solid var(--gold);background:transparent;color:var(--gold);border-radius:10px;padding:0 16px;font-family:'IBM Plex Mono',monospace;font-size:13px;cursor:pointer;text-transform:uppercase;letter-spacing:.08em;transition:background .15s}
          #ucs-workshop .w-cbtn:hover{background:rgba(232,179,60,.12)}
          #ucs-workshop .w-countdown{display:flex;gap:8px;justify-content:center;margin:20px 0 0}
          #ucs-workshop .w-cd{min-width:66px;border:1px solid var(--line);border-radius:10px;padding:9px 6px;text-align:center;background:linear-gradient(180deg,rgba(232,179,60,.08),transparent)}
          #ucs-workshop .w-cd b{display:block;font-family:'IBM Plex Mono',monospace;font-size:24px;font-weight:600;color:var(--gold-lt);line-height:1.1}
          #ucs-workshop .w-cd i{display:block;font-style:normal;font-family:'IBM Plex Mono',monospace;font-size:9.5px;letter-spacing:.16em;color:var(--muted);text-transform:uppercase;margin-top:3px}
          #ucs-workshop .w-sec{padding:56px 0}
          #ucs-workshop .w-sechead{text-align:center;margin-bottom:34px}
          #ucs-workshop .w-sechead h2{margin:10px 0 12px}
          #ucs-workshop .w-sechead p{margin:0 auto;max-width:60ch;color:#CBBFA8}
          #ucs-workshop .w-rule{width:100%;height:1px;background:linear-gradient(90deg,transparent,var(--line),transparent)}
          #ucs-workshop .w-grid2{display:grid;grid-template-columns:1fr;gap:16px}
          @media(min-width:760px){#ucs-workshop .w-grid2{grid-template-columns:1fr 1fr}}
          #ucs-workshop .w-grid4{display:grid;grid-template-columns:1fr;gap:14px}
          @media(min-width:640px){#ucs-workshop .w-grid4{grid-template-columns:1fr 1fr}}
          @media(min-width:1000px){#ucs-workshop .w-grid4{grid-template-columns:repeat(4,1fr)}}
          #ucs-workshop .w-box{border:1px solid var(--line);border-radius:14px;background:linear-gradient(180deg,#14100A,#0D0A05);padding:22px 20px}
          #ucs-workshop .w-box h3{font-family:'Anton',sans-serif;text-transform:uppercase;font-size:19px;letter-spacing:.4px;margin-bottom:8px;color:var(--gold-lt)}
          #ucs-workshop .w-dayno{font-family:'IBM Plex Mono',monospace;font-size:11px;letter-spacing:.2em;color:var(--gold);display:flex;align-items:center;gap:10px;text-transform:uppercase}
          #ucs-workshop .w-dayno::after{content:"";flex:1;height:1px;background:var(--line)}
          #ucs-workshop .w-day h3{font-family:'Anton',sans-serif;text-transform:uppercase;font-size:23px;margin:12px 0 4px;letter-spacing:.4px;color:var(--text)}
          #ucs-workshop .w-day .when{font-family:'IBM Plex Mono',monospace;font-size:12px;color:var(--muted);margin:0 0 14px}
          #ucs-workshop ul.w-list{margin:0;padding-left:0;list-style:none}
          #ucs-workshop ul.w-list li{position:relative;padding-left:24px;margin-bottom:10px;font-size:15.5px;color:#D6CAB3}
          #ucs-workshop ul.w-list li::before{content:"";position:absolute;left:2px;top:8px;width:7px;height:7px;transform:rotate(45deg);border:1px solid var(--gold);background:rgba(232,179,60,.25)}
          #ucs-workshop ul.w-check li::before{content:"✓";position:absolute;left:2px;top:0;color:var(--bull);font-weight:700;border:0;background:none;transform:none;width:auto;height:auto}
          #ucs-workshop .w-step{display:flex;gap:14px;align-items:flex-start}
          #ucs-workshop .w-stepno{font-family:'Anton',sans-serif;font-size:22px;color:var(--gold);min-width:34px}
          #ucs-workshop .w-chip{font-family:'IBM Plex Mono',monospace;font-size:11.5px;letter-spacing:.08em;border:1px solid var(--line);border-radius:999px;padding:6px 12px;color:var(--gold-lt);background:rgba(232,179,60,.06);display:inline-block;margin:4px 6px 0 0}
          #ucs-workshop .w-faq{max-width:760px;margin:0 auto}
          #ucs-workshop .w-q{border-bottom:1px solid var(--line)}
          #ucs-workshop .w-q button{width:100%;background:none;border:0;color:var(--text);text-align:left;cursor:pointer;padding:18px 34px 18px 0;font:inherit;font-weight:600;font-size:16.5px;position:relative}
          #ucs-workshop .w-q button::after{content:"+";position:absolute;right:6px;top:16px;color:var(--gold);font-size:22px;transition:transform .2s}
          #ucs-workshop .w-q.open button::after{transform:rotate(45deg)}
          #ucs-workshop .w-q .a{overflow:hidden;transition:max-height .3s ease}
          #ucs-workshop .w-q .a p{margin:0 0 17px;color:#C3B69E;font-size:15.5px}
          #ucs-workshop .w-final{border:1px solid rgba(232,179,60,.4);border-radius:20px;padding:40px 22px;text-align:center;background:radial-gradient(70% 120% at 50% 0%,rgba(196,140,20,.22),transparent 70%),#100C06}
          #ucs-workshop .w-final .w-btn{max-width:420px;margin:22px auto 0}
          #ucs-workshop .w-disc{border-top:1px solid var(--line);padding:28px 0 40px}
          #ucs-workshop .w-disc p{font-size:12.5px;color:#8B7F68;line-height:1.7;margin:0}
          #ucs-workshop .w-sticky{position:fixed;left:0;right:0;bottom:0;z-index:80;background:rgba(12,9,4,.96);backdrop-filter:blur(8px);border-top:1px solid var(--line);padding:10px 16px;display:flex;align-items:center;justify-content:space-between;gap:12px}
          @media(min-width:960px){#ucs-workshop .w-sticky{display:none}}
        `}</style>

        <div className="w-ticker" aria-hidden="true">
          <div className="w-track">
            {[0, 1].map((k) => (
              <React.Fragment key={k}>
                <span>Live Online · 2 Days</span>
                <span>2+ Ready-to-Use Scanners</span>
                <span>Premium Telegram Community</span>
                <span>Educational · Not Investment Advice</span>
                <span>Language · Hindi</span>
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* HERO */}
        <section className="w-hero">
          <div className="w-wrap w-herogrid">
            <div>
              <span className="w-eyebrow">2-Day Live Workshop</span>
              <h1 className="w-display" style={{ margin: '14px 0 16px' }} data-testid="workshop-title">
                <span className="w-gold">{info?.title || 'Stock Options Strategy'}</span>
              </h1>
              <p className="w-lede">
                A focused, hands-on workshop designed to take you from confusion to a clear,
                rule-based intraday options approach — with live examples and ready-to-use scanners.
              </p>

              <div className="w-facts">
                <div className="w-fact"><div className="k">Dates</div><div className="v">24 &amp; 25 September 2026</div></div>
                <div className="w-fact"><div className="k">Time</div><div className="v">5:00 – 7:00 PM IST</div></div>
                <div className="w-fact"><div className="k">Live Sessions</div><div className="v">{info?.sessions || 2} Live</div></div>
                <div className="w-fact"><div className="k">Scanners</div><div className="v">{info?.scanners || '2+'} Ready-to-Use</div></div>
                <div className="w-fact"><div className="k">Language</div><div className="v">{info?.language || 'Hindi'}</div></div>
                <div className="w-fact"><div className="k">Community</div><div className="v">Premium Telegram</div></div>
              </div>
            </div>

            {/* REGISTRATION / PAYMENT CARD */}
            <div className="w-card" ref={formRef}>
              {success ? (
                <div data-testid="workshop-success" style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 46, marginBottom: 8 }}>🎉</div>
                  <div className="w-display" style={{ fontSize: 26, color: 'var(--gold-lt)' }}>You&apos;re enrolled!</div>
                  <p className="w-muted" style={{ margin: '12px 0 18px' }}>
                    Payment successful. A confirmation email is on its way. Join the workshop
                    WhatsApp group below to get your live joining link.
                  </p>
                  <a href={success.whatsapp_link} target="_blank" rel="noopener noreferrer"
                    data-testid="workshop-whatsapp-btn" className="w-btn"
                    style={{ background: 'linear-gradient(180deg,#3ee27f,#1eb457)', color: '#062b12', textDecoration: 'none' }}>
                    Join the Workshop WhatsApp Group
                  </a>
                  <div style={{ marginTop: 16, padding: '14px', border: '1px solid var(--line)', borderRadius: 12, background: '#0B0803' }}>
                    <p className="w-mono" style={{ fontSize: 12, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.12em', margin: '0 0 6px' }}>Your Login Access</p>
                    <p style={{ fontSize: 14, color: '#D6CAB3', margin: '0 0 12px' }}>
                      You now have an account. Log in with your phone
                      {form.phone ? <> (<b className="w-gold">{form.phone}</b>)</> : null} to see
                      your <b>Workshop Enrolled</b> status and the group link anytime.
                    </p>
                    <a href="/login" data-testid="workshop-login-btn" className="w-btn"
                      style={{ fontSize: 15, padding: '12px 16px', textDecoration: 'none' }}>
                      Log in to your Dashboard
                    </a>
                  </div>
                </div>
              ) : (
                <>
                  <div className="w-price">
                    <span className="now" data-testid="workshop-price">{rupee(payable)}</span>
                    <span className="was">{rupee(mrp)}</span>
                    <span className="off">{Math.round((1 - payable / mrp) * 100)}% OFF</span>
                  </div>
                  <p className="w-mono" style={{ fontSize: 12, color: 'var(--muted)', margin: '6px 0 0' }}>
                    {applied?.valid
                      ? `Coupon ${applied.code} applied · you save ${rupee(mrp - payable)}`
                      : 'Have a coupon? Apply it below for an extra discount.'}
                  </p>

                  <div className="w-countdown">
                    {[['Days', cd.d], ['Hrs', cd.h], ['Min', cd.m], ['Sec', cd.s]].map(([l, v]) => (
                      <div className="w-cd" key={l}><b>{String(v).padStart(2, '0')}</b><i>{l}</i></div>
                    ))}
                  </div>

                  <div style={{ marginTop: 20 }}>
                    <label htmlFor="w-name">Full Name</label>
                    <input id="w-name" data-testid="workshop-name" placeholder="Your full name" value={form.name} onChange={setField('name')} />
                  </div>
                  <div style={{ marginTop: 12 }}>
                    <label htmlFor="w-phone">Phone (WhatsApp)</label>
                    <input id="w-phone" data-testid="workshop-phone" placeholder="10-digit mobile number" value={form.phone} onChange={setField('phone')} />
                  </div>
                  <div style={{ marginTop: 12 }}>
                    <label htmlFor="w-email">Email</label>
                    <input id="w-email" data-testid="workshop-email" type="email" placeholder="you@email.com" value={form.email} onChange={setField('email')} />
                  </div>

                  <div style={{ marginTop: 12 }}>
                    <label htmlFor="w-coupon">Coupon Code</label>
                    {applied?.valid ? (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, background: '#0B0803', border: '1px solid rgba(47,191,110,.4)', borderRadius: 10, padding: '11px 14px' }} data-testid="workshop-coupon-applied">
                        <span className="w-mono" style={{ color: 'var(--bull)', fontSize: 14 }}>✓ {applied.code} · −{rupee(applied.discount_amount)}</span>
                        <button type="button" onClick={removeCoupon} className="w-mono" style={{ background: 'none', border: 0, color: 'var(--muted)', cursor: 'pointer', fontSize: 12, textTransform: 'uppercase' }} data-testid="workshop-coupon-remove">Remove</button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: 8 }}>
                        <input id="w-coupon" data-testid="workshop-coupon-input" placeholder="Enter code" value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} />
                        <button type="button" className="w-cbtn" onClick={applyCoupon} disabled={applyingCoupon || !coupon.trim()} data-testid="workshop-coupon-apply">
                          {applyingCoupon ? '...' : 'Apply'}
                        </button>
                      </div>
                    )}
                  </div>

                  <button className="w-btn" style={{ marginTop: 20 }} onClick={handlePay} disabled={processing} data-testid="workshop-pay-btn">
                    {processing ? 'Processing…' : `Enroll Now · ${rupee(payable)}`}
                  </button>
                  <p style={{ textAlign: 'center', fontSize: 12, color: 'var(--muted)', margin: '10px 0 0' }}>
                    Secure payment via Razorpay · Non-refundable
                  </p>
                </>
              )}
            </div>
          </div>
        </section>

        <div className="w-wrap"><div className="w-rule" /></div>

        {/* WHAT YOU'LL LEARN */}
        <section className="w-sec">
          <div className="w-wrap">
            <div className="w-sechead">
              <span className="w-eyebrow">Outcomes</span>
              <h2 className="w-display">What you will be able to do</h2>
              <p>Two focused sessions built around one goal — trading stock options with a clear, repeatable process.</p>
            </div>
            <div className="w-grid4">
              {OUTCOMES.map((o, i) => (
                <div className="w-box" key={i} data-testid={`workshop-outcome-${i}`}>
                  <h3>{o.t}</h3>
                  <p className="w-muted" style={{ fontSize: 15, margin: 0 }}>{o.d}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* OVERVIEW */}
        <section className="w-sec" style={{ background: '#0C0905' }}>
          <div className="w-wrap">
            <div className="w-sechead">
              <span className="w-eyebrow">Workshop Overview</span>
              <h2 className="w-display">The essentials</h2>
            </div>
            <div className="w-grid4">
              {[['Starts', '24 September 2026'], ['Duration', '2 Days'], ['Sessions', `${info?.sessions || 2} Live Online`], ['Scanners', `${info?.scanners || '2+'} Ready-to-Use`]].map(([k, v]) => (
                <div className="w-box" key={k} style={{ textAlign: 'center' }}>
                  <div className="w-mono" style={{ fontSize: 11, letterSpacing: '.18em', color: 'var(--muted)', textTransform: 'uppercase' }}>{k}</div>
                  <div className="w-display" style={{ fontSize: 22, color: 'var(--gold-lt)', marginTop: 8 }}>{v}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CURRICULUM */}
        <section className="w-sec">
          <div className="w-wrap">
            <div className="w-sechead">
              <span className="w-eyebrow">Curriculum</span>
              <h2 className="w-display">What we&apos;ll cover</h2>
              <p>A clear day-by-day plan — foundation first, then live strategy and execution.</p>
            </div>
            <div className="w-grid2">
              {DAYS.map((d, i) => (
                <div className="w-box w-day" key={i} data-testid={`workshop-day-${i}`}>
                  <div className="w-dayno">{d.no}</div>
                  <h3>{d.title}</h3>
                  <p className="when">{d.when}</p>
                  <ul className="w-list">
                    {d.points.map((p, j) => <li key={j}>{p}</li>)}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* WHO IS THIS FOR */}
        <section className="w-sec" style={{ background: '#0C0905' }}>
          <div className="w-wrap">
            <div className="w-sechead">
              <span className="w-eyebrow">Who it&apos;s for</span>
              <h2 className="w-display">Is this workshop for you?</h2>
            </div>
            <div className="w-grid2">
              <div className="w-box">
                <ul className="w-list w-check">
                  {AUDIENCE.map((a, i) => <li key={i} data-testid={`workshop-audience-${i}`}>{a}</li>)}
                </ul>
              </div>
              <div className="w-box">
                <h3>Before you join</h3>
                <ul className="w-list">
                  <li>Basic knowledge of stock market concepts &amp; terminology</li>
                  <li>Understanding of orders, stop-loss and risk/reward</li>
                  <li>How to place buy/sell trades through your broker</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* INSTRUCTOR */}
        <section className="w-sec">
          <div className="w-wrap">
            <div className="w-sechead">
              <span className="w-eyebrow">Your Instructor</span>
              <h2 className="w-display">Chirag Rathod</h2>
            </div>
            <div className="w-box" style={{ maxWidth: 760, margin: '0 auto', textAlign: 'center' }} data-testid="workshop-instructor">
              <p className="w-lede" style={{ margin: '0 auto' }}>
                He is a NISM-registered Research Analyst with an MBA in Finance from Mumbai University.
                With over 6 years of trading experience, he has successfully trained 1,000+ students in
                swing trading, options, and futures, helping them develop practical market skills and
                trading confidence.
              </p>
              <div style={{ marginTop: 16 }}>
                <span className="w-chip">Research Analyst</span>
                <span className="w-chip">6+ Years Experience</span>
                <span className="w-chip">NISM Registered</span>
                <span className="w-chip">1,000+ Students Trained</span>
              </div>
            </div>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section className="w-sec" style={{ background: '#0C0905' }}>
          <div className="w-wrap">
            <div className="w-sechead">
              <span className="w-eyebrow">How it works</span>
              <h2 className="w-display">How the workshop runs</h2>
            </div>
            <div className="w-grid2">
              {STEPS.map((s, i) => (
                <div className="w-box" key={i}>
                  <div className="w-step">
                    <span className="w-stepno">{String(i + 1).padStart(2, '0')}</span>
                    <p style={{ margin: 0, color: '#D6CAB3', fontSize: 15.5 }}>{s}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="w-sec">
          <div className="w-wrap">
            <div className="w-sechead">
              <span className="w-eyebrow">FAQ</span>
              <h2 className="w-display">Frequently asked questions</h2>
            </div>
            <div className="w-faq">
              {FAQS.map((f, i) => (
                <div className={`w-q${openFaq === i ? ' open' : ''}`} key={i}>
                  <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)} data-testid={`workshop-faq-${i}`}>{f.q}</button>
                  <div className="a" style={{ maxHeight: openFaq === i ? 200 : 0 }}>
                    <p>{f.a}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="w-sec" style={{ paddingTop: 0 }}>
          <div className="w-wrap">
            <div className="w-final">
              <span className="w-eyebrow">Limited Seats</span>
              <h2 className="w-display" style={{ margin: '10px 0 6px' }}>
                <span className="w-gold">Reserve your seat</span>
              </h2>
              <div className="w-price" style={{ justifyContent: 'center' }}>
                <span className="now">{rupee(payable)}</span>
                <span className="was">{rupee(mrp)}</span>
              </div>
              <button className="w-btn" onClick={scrollToForm} data-testid="workshop-final-cta">Enroll in the Workshop</button>
            </div>
          </div>
        </section>

        {/* DISCLAIMER */}
        <div className="w-wrap">
          <div className="w-disc">
            <p>
              <b>Disclaimer:</b> The concepts, analyses, and strategies shared in this workshop are for
              educational purposes only and should not be interpreted as financial advice. Upper Circuit
              Stocks is not responsible for any losses resulting from applying the knowledge gained. The
              workshop fee is non-refundable.
            </p>
          </div>
        </div>

        {/* STICKY MOBILE BAR */}
        {!success && (
          <div className="w-sticky">
            <div>
              <div className="w-mono" style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase' }}>Workshop</div>
              <div className="w-display" style={{ fontSize: 22, color: 'var(--gold-lt)' }}>
                {rupee(payable)} <span style={{ fontSize: 14, color: '#8a7d66', textDecoration: 'line-through', fontFamily: 'IBM Plex Mono, monospace' }}>{rupee(mrp)}</span>
              </div>
            </div>
            <button className="w-btn" style={{ width: 'auto', padding: '12px 22px', fontSize: 16 }} onClick={scrollToForm} data-testid="workshop-sticky-cta">Enroll</button>
          </div>
        )}
      </div>
      <SiteFooter />
    </>
  );
}
